import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { FieldDescription, FieldError } from "@ascleo/ui/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupFloatingLabel,
  InputGroupInput,
  InputGroupTextarea,
} from "@ascleo/ui/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ascleo/ui/components/ui/select";
import { cn } from "@ascleo/ui/lib/utils";

/**
 * Kiosk size of the floating label: the `2xl` geometry (56px control) with the
 * floated caption raised from 12px to 16px. The `data-[floating=true]:` variant
 * carries an attribute selector, so it out-specifies the size's `text-xs`.
 */
const KIOSK_FLOATING_LABEL_CLASS =
  "data-[floating=true]:text-base data-[floating=true]:px-2";

/**
 * Props shared by every kiosk field.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const base: KioskFieldBaseProps = {
 *   id: "checkin-lookup-lastname",
 *   label: t("self-service.check-in.patient-lookup.lastname"),
 *   icon: UserRound,
 *   testId: "frontdesk-patient-lookup-lastname-input",
 * };
 * ```
 */
export interface KioskFieldBaseProps {
  /** Unique DOM id of the control; also binds the floating label. */
  readonly id: string;
  /** Translated label; floats above the value once filled or focused. */
  readonly label: ReactNode;
  /** Leading intent icon (decorative, `aria-hidden`). */
  readonly icon: LucideIcon;
  /** `data-testid` of the control. */
  readonly testId: string;
  /** Marks the field required (`aria-required` plus a visual marker). */
  readonly required?: boolean;
  /** Translated error message; sets `aria-invalid` and links the message. */
  readonly error?: ReactNode;
  /** `data-testid` of the error message; defaults to `${testId}-error`. */
  readonly errorTestId?: string;
  /** Persistent hint below the control (format hints such as TT.MM.JJJJ). */
  readonly description?: ReactNode;
  readonly disabled?: boolean;
}

function describedBy(id: string, hasDescription: boolean, hasError: boolean) {
  const ids = [
    hasDescription ? `${id}-description` : null,
    hasError ? `${id}-error` : null,
  ].filter((value) => value !== null);
  return ids.length > 0 ? ids.join(" ") : undefined;
}

/**
 * The label, description and error around a kiosk input group. Exposed so a
 * specialised control (date of birth, insurer search) can follow the same
 * standard; {@link KioskTextField} and friends use it internally.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskFieldFrame id="dob" description={hint} error={error} testId="dob-input">
 *   <DateOfBirthInput id="dob" dataTestId="dob-input" value={v} onChange={set} />
 * </KioskFieldFrame>
 * ```
 */
export function KioskFieldFrame({
  id,
  testId,
  description,
  error,
  errorTestId,
  children,
}: {
  readonly id: string;
  readonly testId: string;
  readonly description?: ReactNode;
  readonly error?: ReactNode;
  readonly errorTestId?: string;
  readonly children: ReactNode;
}) {
  return (
    <div data-slot="kiosk-field" className="flex min-w-0 flex-col gap-2 pt-2">
      {children}
      {description ? (
        <FieldDescription id={`${id}-description`} className="text-base">
          {description}
        </FieldDescription>
      ) : null}
      {error ? (
        <FieldError
          id={`${id}-error`}
          data-testid={errorTestId ?? `${testId}-error`}
          className="text-base text-destructive-ink"
        >
          {error}
        </FieldError>
      ) : null}
    </div>
  );
}

/**
 * The floating label of a kiosk field, with the optional required marker.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskFloatingLabel htmlFor="dob" required>{t("common.dob")}</KioskFloatingLabel>
 * ```
 */
export function KioskFloatingLabel({
  htmlFor,
  required,
  floating,
  className,
  children,
}: {
  readonly htmlFor: string;
  readonly required?: boolean;
  /** Keep the label floated (controls without a native value, e.g. selects). */
  readonly floating?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  return (
    <InputGroupFloatingLabel
      htmlFor={htmlFor}
      size="2xl"
      floating={floating}
      className={cn(KIOSK_FLOATING_LABEL_CLASS, className)}
    >
      {children}
      {required ? (
        <span aria-hidden className="ms-0.5 text-destructive-ink">
          *
        </span>
      ) : null}
    </InputGroupFloatingLabel>
  );
}

function KioskFieldIcon({ icon: Icon }: { readonly icon: LucideIcon }) {
  return (
    <InputGroupAddon align="inline-start" className="ps-4">
      <Icon aria-hidden className="size-6" />
    </InputGroupAddon>
  );
}

/**
 * Props of {@link KioskTextField}.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * type Props = KioskTextFieldProps;
 * ```
 */
export interface KioskTextFieldProps extends KioskFieldBaseProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onBlur?: () => void;
  readonly type?: "text" | "email" | "tel" | "search" | "password" | "url";
  readonly inputMode?: "text" | "email" | "tel" | "search" | "numeric" | "decimal" | "url";
  readonly autoComplete?: string;
  readonly maxLength?: number;
  /** Example value shown only while the label is floated (never at rest). */
  readonly placeholder?: string;
  /** Optional trailing addon, e.g. a clear or reveal `InputGroupButton` (≥48px, with icon). */
  readonly endAddon?: ReactNode;
}

/**
 * Kiosk text input: `InputGroup` (56px) + leading intent icon + floating
 * label (16px when floated) + error/description slot, wired for a TanStack
 * Form field (`value`/`onChange`/`onBlur`, `aria-invalid`, `aria-describedby`).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <form.Field name="lastname">
 *   {(field) => (
 *     <KioskTextField
 *       id="checkin-lookup-lastname"
 *       testId="frontdesk-patient-lookup-lastname-input"
 *       label={t("self-service.check-in.patient-lookup.lastname")}
 *       icon={UserRound}
 *       required
 *       autoComplete="family-name"
 *       value={field.state.value}
 *       onChange={field.handleChange}
 *       onBlur={field.handleBlur}
 *       error={field.state.meta.errors.length > 0 ? t(getFormErrorMessage(field.state.meta.errors[0])) : undefined}
 *     />
 *   )}
 * </form.Field>
 * ```
 */
export function KioskTextField({
  id,
  label,
  icon,
  testId,
  required,
  error,
  errorTestId,
  description,
  disabled,
  value,
  onChange,
  onBlur,
  type = "text",
  inputMode,
  autoComplete,
  maxLength,
  placeholder,
  endAddon,
}: KioskTextFieldProps) {
  const hasError = Boolean(error);
  return (
    <KioskFieldFrame
      id={id}
      testId={testId}
      description={description}
      error={error}
      errorTestId={errorTestId}
    >
      <InputGroup size="2xl">
        <KioskFieldIcon icon={icon} />
        <InputGroupInput
          id={id}
          data-testid={testId}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          maxLength={maxLength}
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          required={required}
          aria-required={required}
          aria-invalid={hasError}
          aria-describedby={describedBy(id, Boolean(description), hasError)}
          className="h-full text-lg"
        />
        <KioskFloatingLabel htmlFor={id} required={required}>
          {label}
        </KioskFloatingLabel>
        {endAddon ? (
          <InputGroupAddon align="inline-end" className="pe-2">
            {endAddon}
          </InputGroupAddon>
        ) : null}
      </InputGroup>
    </KioskFieldFrame>
  );
}

/**
 * Props of {@link KioskTextareaField}.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * type Props = KioskTextareaFieldProps;
 * ```
 */
export interface KioskTextareaFieldProps extends KioskFieldBaseProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onBlur?: () => void;
  readonly maxLength?: number;
  /** Visible text rows before the area grows; defaults to 3. */
  readonly rows?: number;
}

/**
 * Kiosk multi-line input with the same icon, floating label and error
 * standard as {@link KioskTextField}. The icon and the resting label sit on
 * the first line instead of the vertical centre.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskTextareaField
 *   id="prescription-request"
 *   testId="prescription-request-textarea"
 *   label={t("self-service.prescription.request.label")}
 *   icon={Pill}
 *   value={field.state.value}
 *   onChange={field.handleChange}
 *   onBlur={field.handleBlur}
 * />
 * ```
 */
export function KioskTextareaField({
  id,
  label,
  icon: Icon,
  testId,
  required,
  error,
  errorTestId,
  description,
  disabled,
  value,
  onChange,
  onBlur,
  maxLength,
  rows = 3,
}: KioskTextareaFieldProps) {
  const hasError = Boolean(error);
  return (
    <KioskFieldFrame
      id={id}
      testId={testId}
      description={description}
      error={error}
      errorTestId={errorTestId}
    >
      <InputGroup size="2xl" className="items-start">
        <InputGroupAddon align="inline-start" className="self-start ps-4 pt-4">
          <Icon aria-hidden className="size-6" />
        </InputGroupAddon>
        <InputGroupTextarea
          id={id}
          data-testid={testId}
          rows={rows}
          maxLength={maxLength}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          required={required}
          aria-required={required}
          aria-invalid={hasError}
          aria-describedby={describedBy(id, Boolean(description), hasError)}
          className="min-h-32 px-4 py-4 text-lg"
        />
        <KioskFloatingLabel
          htmlFor={id}
          required={required}
          className="data-[floating=false]:top-7"
        >
          {label}
        </KioskFloatingLabel>
      </InputGroup>
    </KioskFieldFrame>
  );
}

/**
 * One option of a {@link KioskSelectField}.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const option: KioskSelectOption = { value: "de", label: "Deutsch" };
 * ```
 */
export interface KioskSelectOption {
  readonly value: string;
  readonly label: string;
}

/**
 * Props of {@link KioskSelectField}.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * type Props = KioskSelectFieldProps;
 * ```
 */
export interface KioskSelectFieldProps extends KioskFieldBaseProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly options: readonly KioskSelectOption[];
  /** Shown while no option is selected. */
  readonly placeholder?: string;
}

/**
 * Kiosk single-select: the `@ascleo/ui` Select trigger inside the same 56px
 * input group with leading icon and a permanently floated label (a trigger
 * has no native value to detect). Options are 56px tall for touch. Prefer a
 * choice group of buttons for 2–4 options (FI-124).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskSelectField
 *   id="settings-default-language"
 *   testId="frontdesk-settings-default-kiosk-language"
 *   label={t("settings.appearance.default-kiosk-language")}
 *   icon={Languages}
 *   value={field.state.value}
 *   onValueChange={field.handleChange}
 *   options={FRONTDESK_LANGUAGE_CATALOG.map(({ code, label }) => ({ value: code, label }))}
 * />
 * ```
 */
export function KioskSelectField({
  id,
  label,
  icon,
  testId,
  required,
  error,
  errorTestId,
  description,
  disabled,
  value,
  onValueChange,
  options,
  placeholder,
}: KioskSelectFieldProps) {
  const hasError = Boolean(error);
  const selected = options.find((option) => option.value === value);
  return (
    <KioskFieldFrame
      id={id}
      testId={testId}
      description={description}
      error={error}
      errorTestId={errorTestId}
    >
      <InputGroup size="2xl">
        <KioskFieldIcon icon={icon} />
        <Select
          value={value}
          disabled={disabled}
          onValueChange={(next) => {
            if (typeof next === "string") onValueChange(next);
          }}
        >
          <SelectTrigger
            id={id}
            data-testid={testId}
            aria-required={required}
            aria-invalid={hasError}
            aria-describedby={describedBy(id, Boolean(description), hasError)}
            className="h-full w-full min-w-0 flex-1 rounded-none border-0 bg-transparent ps-4 pe-4 text-lg shadow-none focus-visible:ring-0 data-[size=default]:h-full dark:bg-transparent [&>svg]:size-6"
          >
            <SelectValue>
              {selected?.label ?? (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="min-h-14 text-lg"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <KioskFloatingLabel htmlFor={id} required={required} floating>
          {label}
        </KioskFloatingLabel>
      </InputGroup>
    </KioskFieldFrame>
  );
}
